import {
  useGetAdminCampaignMessages,
  useGetAdminSsoErrors
} from '@ijoin/hooks';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import KeyIcon from '@mui/icons-material/Key';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import PaymentsIcon from '@mui/icons-material/Payments';
/* import PeopleIcon from '@mui/icons-material/People'; */
import QueueIcon from '@mui/icons-material/Queue';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SavingsIcon from '@mui/icons-material/Savings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SchemaIcon from '@mui/icons-material/Schema';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link, useLocation } from '@tanstack/react-router';

import { FC, Fragment, ReactNode } from 'react';
import { useToggle } from 'usehooks-ts';

export const FAILED_CAMPAIGNS_LAST_CHECKED_KEY = 'lastCheckedFailedCampaigns';
export const FAILED_CAMPAIGNS_BADGE_QUERY_KEY = 'navFailedCampaignsBadge';
export const UNCLEARED_SSO_ERRORS_BADGE_QUERY_KEY =
  'navUnclearedSsoErrorsBadge';

const NAV_ITEMS = [
  { href: '/dashboard', icon: <DashboardIcon />, label: 'Dashboard' },
  {
    href: '/activity-monitor',
    icon: <TimelineIcon />,
    label: 'Activity Monitor'
  },
  {
    href: '/organizations',
    icon: <BusinessIcon />,
    label: 'Advisory Organizations'
  },
  {
    href: '/annuity-account-errors',
    icon: <AssignmentLateIcon />,
    label: 'Annuity Account Errors'
  },
  {
    href: '/carriers',
    icon: <CorporateFareIcon />,
    label: 'Annuity Carriers'
  },
  {
    href: '/api-keys',
    icon: <KeyIcon />,
    label: 'API Keys',
    superAdminOnly: true
  },
  {
    href: '/auto-force-outs',
    icon: <PaymentsIcon />,
    label: 'Auto Force Outs',
    superAdminOnly: true
  },
  {
    href: '/campaigns',
    icon: <CampaignIcon />,
    label: 'Email Campaigns',
    superAdminOnly: true
  },
  {
    href: '/integrations',
    icon: <IntegrationInstructionsIcon />,
    label: 'Integrations'
  },
  {
    href: '/ira-clarity',
    icon: <SavingsIcon />,
    label: 'IRA Clarity',
    superAdminOnly: true
  },
  {
    href: '/methodologies',
    icon: <ShowChartIcon />,
    label: 'Methodologies'
  },
  /* { href: '/participants', icon: <PeopleIcon />, label: 'Participants' }, */
  {
    href: '/recordkeepers',
    icon: <AccountBalanceIcon />,
    label: 'Record Keepers'
  },
  { href: '/sso-errors', icon: <KeyOffIcon />, label: 'SSO Errors' },
  {
    href: '/submission-log',
    icon: <HistoryIcon />,
    label: 'Submission Log',
    superAdminOnly: true
  },
  {
    href: '/participant-transactions',
    icon: <ReceiptLongIcon />,
    label: 'Transactions'
  },
  { href: '/users', icon: <GroupIcon />, label: 'Users', superAdminOnly: true }
];

const SYSTEM_ITEMS = [
  {
    href: '/connectivity',
    icon: <NetworkCheckIcon />,
    label: 'Connectivity'
  },
  { href: '/cron-jobs', icon: <ScheduleIcon />, label: 'Cron Jobs' },
  { href: '/db-insights', icon: <SchemaIcon />, label: 'DB Insights' },
  { href: '/flyway', icon: <StorageIcon />, label: 'Flyway Migrations' },
  { href: '/queues', icon: <QueueIcon />, label: 'Queues' },
  {
    href: '/relationship-health',
    icon: <MonitorHeartIcon />,
    label: 'Relationship Health'
  },
  { href: '/s3-browser', icon: <FolderIcon />, label: 'S3 Browser' }
];

const DRAWER_WIDTH_FULL = 280;
const DRAWER_WIDTH_MINI = 64;

export const NavMenu: FC<{
  onClose?: () => void;
  open?: boolean;
}> = props => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const location = useLocation();
  const [isSystemOpen, toggleSystemOpen] = useToggle(
    SYSTEM_ITEMS.some(item => location.pathname.startsWith(item.href))
  );
  const isAdmin = localStorage.getItem('isAdmin') === '1';

  const isCollapsed = isMd && !isXl;
  const drawerWidth = isCollapsed ? DRAWER_WIDTH_MINI : DRAWER_WIDTH_FULL;

  const lastChecked = localStorage.getItem(FAILED_CAMPAIGNS_LAST_CHECKED_KEY);

  const failedCampaigns = useGetAdminCampaignMessages(
    {
      skip: 0,
      take: 1,
      ...(lastChecked && { processedAtFrom: lastChecked }),
      status: ['Failed' as const]
    },
    {
      query: {
        enabled: isAdmin,
        queryKey: [FAILED_CAMPAIGNS_BADGE_QUERY_KEY],
        refetchInterval: 60_000
      }
    }
  );
  const failedCount = failedCampaigns.data?.pagination?.total ?? 0;

  const unclearedSsoErrors = useGetAdminSsoErrors(
    { cleared: false, skip: 0, take: 1 },
    {
      query: {
        enabled: true,
        queryKey: [UNCLEARED_SSO_ERRORS_BADGE_QUERY_KEY],
        refetchInterval: 60_000
      }
    }
  );
  const unclearedSsoErrorCount =
    unclearedSsoErrors.data?.pagination?.total ?? 0;

  const renderItem = (
    item: { href: string; icon: ReactNode; label: string },
    nested?: boolean
  ) => {
    const badgeCount =
      item.href === '/campaigns'
        ? failedCount
        : item.href === '/sso-errors'
          ? unclearedSsoErrorCount
          : 0;

    const iconNode = badgeCount ? (
      <Badge
        badgeContent={badgeCount}
        color='error'
        data-testid={
          item.href === '/campaigns'
            ? 'failedCampaignsBadge'
            : 'unclearedSsoErrorsBadge'
        }
        max={99}>
        {item.icon}
      </Badge>
    ) : (
      item.icon
    );

    const button = (
      <ListItemButton
        component={Link}
        data-component='link'
        data-testid={item.label}
        selected={location.pathname.startsWith(item.href)}
        sx={{
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: theme.palette.action.selected
            },
            backgroundColor: theme.palette.action.selected,
            borderRight: `3px solid ${theme.palette.primary.main}`
          },
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          minHeight: 48,
          pl: isCollapsed ? 0 : nested ? 5 : 3,
          pr: isCollapsed ? 0 : 3
        }}
        to={item.href}>
        <ListItemIcon
          sx={{
            justifyContent: 'center',
            minWidth: isCollapsed ? 0 : 36
          }}>
          {iconNode}
        </ListItemIcon>
        {!isCollapsed && <ListItemText primary={item.label} />}
      </ListItemButton>
    );

    if (isCollapsed) {
      return (
        <Tooltip key={item.href} placement='right' title={item.label}>
          {button}
        </Tooltip>
      );
    }

    return <Fragment key={item.href}>{button}</Fragment>;
  };

  const drawerContent = (
    <Stack sx={{ height: '100%', width: drawerWidth }}>
      {!isMd && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={props.onClose}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      )}
      <List sx={{ flexGrow: 1, overflowY: 'auto', pt: isMd ? 2 : 0 }}>
        {(isAdmin
          ? NAV_ITEMS
          : NAV_ITEMS.filter(item => !item.superAdminOnly)
        ).map(item => renderItem(item))}
        {isAdmin && isCollapsed && (
          <>
            <Divider sx={{ my: 1 }} />
            {SYSTEM_ITEMS.map(item => renderItem(item))}
          </>
        )}
        {isAdmin && !isCollapsed && (
          <>
            <ListItemButton
              data-testid='systemGroup'
              onClick={toggleSystemOpen}
              sx={{ minHeight: 48, px: 3 }}>
              <ListItemText
                primary='System'
                slotProps={{ primary: { fontWeight: 600 } }}
              />
              {isSystemOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={isSystemOpen} timeout='auto' unmountOnExit>
              <List component='div' disablePadding>
                {SYSTEM_ITEMS.map(item => renderItem(item, true))}
              </List>
            </Collapse>
          </>
        )}
      </List>
      <Typography
        color='text.secondary'
        data-testid='appVersion'
        sx={{ p: 2, textAlign: 'center' }}
        variant='body2'>
        v{__APP_VERSION__}
      </Typography>
    </Stack>
  );

  if (isMd) {
    const widthTransition = theme.transitions.create('width', {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.sharp
    });

    return (
      <Drawer
        PaperProps={{
          sx: {
            overflowX: 'hidden',
            position: 'relative',
            transition: widthTransition,
            width: drawerWidth
          }
        }}
        sx={{
          flexShrink: 0,
          transition: widthTransition,
          width: drawerWidth
        }}
        variant='permanent'>
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      onClose={props.onClose}
      open={props.open ?? false}
      variant='temporary'>
      {drawerContent}
    </Drawer>
  );
};

NavMenu.displayName = 'NavMenu';
